import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/redux/store";

/**
 * Unsent message text, keyed by conversation address.
 *
 * Not ChatInputs' local state: a single `message` string there is shared by every
 * conversation, so a draft followed you when you switched. It also has to outlive the
 * component, which unmounts whenever no conversation is open (below md that's just the back
 * button). In memory only — like the conversation cache, a page refresh starts clean.
 */
interface DraftsState {
  byAddress: Record<string, string>;
}

const initialState: DraftsState = { byAddress: {} };

// controlSlice's deleteConversation thunk, by action type (see extraReducers below).
const DELETE_CONVERSATION_FULFILLED = "chat/deleteConversation/fulfilled" as const;

const draftsSlice = createSlice({
  name: "drafts",
  initialState,
  reducers: {
    setDraft: (state, action: PayloadAction<{ address: string; text: string }>) => {
      const { address, text } = action.payload;
      // Drop empties rather than storing "" — keeps the map to conversations actually holding
      // a draft, which a "has a draft" indicator in the conversation list could read directly.
      if (text) state.byAddress[address] = text;
      else delete state.byAddress[address];
    },
    clearDraft: (state, action: PayloadAction<string>) => {
      delete state.byAddress[action.payload];
    },
  },
  extraReducers: (builder) => {
    // Never leave one user's typing for the next.
    builder.addCase("auth/logout/fulfilled", (state) => {
      state.byAddress = {};
    });
    builder.addCase(
      DELETE_CONVERSATION_FULFILLED,
      (state, action: PayloadAction<{ address: string }, typeof DELETE_CONVERSATION_FULFILLED>) => {
        delete state.byAddress[action.payload.address];
      }
    );
  },
});

export const { setDraft, clearDraft } = draftsSlice.actions;

export const selectDraft = (state: RootState, address: string) =>
  state.drafts.byAddress[address] ?? "";

export default draftsSlice.reducer;
